import SubstanceDialogHtml from "./SubstanceDialogHtml.js";
import UploadStructureCmp from "./UploadStructureCmp.js";
import SubstanceValidationError from "../../js/entities/error/SubstanceValidationError.js";
import SubstanceDialogValidator from "./SubstanceDialogValidator.js";
import BaseDialogCmp from "../BaseDialog/BaseDialogCmp.js";
import ValidationError from "../../js/entities/error/ValidationError.js";
import SubstanceExtractionParams from "../massSpec/SubstanceExtractionParams.js";
import InjectionSearchDialogCmp from "../InjectionSearchDialog/InjectionSearchDialogCmp.js";
import InjectionSearchRepository from "../InjectionSearchDialog/InjectionSearchRepository.js";
import StructureMeta from "../../js/entities/StructureMeta.js";
import StructureMetaList from "../../js/entities/StructureMetaList.js";
import Messages from "../../js/util/Messages.js";

export default class SubstanceDialogCmp {
    /** @type {SubstanceDialogHtml} */
    #html = new SubstanceDialogHtml();
    /** @type {UploadStructureCmp} */
    #uploadStructureComponent;
    /** @type {ColorPalette} */
    #colorPalette;
    /** @type {BaseDialogCmp} */
    #notificationDialog = new BaseDialogCmp(
        {title: "Structure parsing error", btnCancel: null});
    /** @type {SubstanceRepository} */
    #substanceRepository;
    /** @type {InjectionRepository} */
    #injectionRepository;
    /** @type {StructureSvgLoader} */
    #structureSvgLoader;
    /** @type {UserSettingsInBrowserStorage} */
    #userSettings;
    /** @type {{orgId: string, injectionId: string}} where the analytes would be imported into */
    #importTarget = {orgId: null, injectionId: null};
    /** @type {SubstanceDialogValidator} */
    #substanceValidator = new SubstanceDialogValidator();
    /** @type {AliasColorScheme} */
    #aliasColorScheme;
    /** @type {SubstanceDialogFormData} */
    #formData;
    /** @type {SubstanceExtractionParams} */
    #extractionParams;
    #callbacks = {
        onMultipleStructuresUploaded: [],
        onSaveOrUpdate: [],
        onDelete: [],
        onMassSpecBtnClick: [],
    };

    /**
     * @param {SubstanceRepository} substanceRepository
     * @param {StructureSvgLoader} structureSvgLoader
     * @param {InjectionRepository} injectionRepository - to find the injection to import analytes from
     * @param {UserSettingsInBrowserStorage} userSettings - remembers the injections imported from before
     */
    constructor(substanceRepository, structureSvgLoader, injectionRepository, userSettings) {
        this.#substanceRepository = substanceRepository;
        this.#injectionRepository = injectionRepository;
        this.#structureSvgLoader = structureSvgLoader;
        this.#userSettings = userSettings;
        this.#uploadStructureComponent = new UploadStructureCmp(substanceRepository, structureSvgLoader);
    }

    /**
     * @param {AliasColorScheme} aliasColorScheme
     * @param {string} orgId
     * @param {string} injectionId
     */
    init(aliasColorScheme, {orgId = null, injectionId = null} = {}) {
        this.#aliasColorScheme = aliasColorScheme;
        this.#importTarget = {orgId, injectionId};
    }

    /**
     * @param {SubstanceDialogFormData} formData
     * @param {SubstanceExtractionParams} substanceExtractionParams
     */
    async show(formData, substanceExtractionParams) {
        this.#formData = formData;
        this.#html.show(formData);
        if (!formData.color)
            this.#onDisplayNameChange();
        this.#initHtmlDependantComponents();
        this.#substanceValidator.init(this.#substanceRepository, formData.existingDisplayNames);
        this.#uploadStructureComponent.show({isReadOnly: formData.isReadOnly()});
        this.#setEventListeners();
        if (formData.structureSvgPath)
            await this.#uploadStructureComponent.loadStructure(formData.structureSvgPath,formData.isReadOnly());
        this.#extractionParams = substanceExtractionParams;
    }

    /** Removes the dialog from the DOM. */
    remove() {
        this.#html.remove();
    }

    /**
     * Callback to be called when the user clicks on "delete" button.
     * @param {function} callback
     */
    onDelete(callback) {
        this.#callbacks.onDelete.push(callback);
    }
    /**
     * Callback to be called when the user uploads structure list instead of a single structure.
     * @param {function} callback
     */
    onMultipleStructuresUploaded(callback) {
        this.#callbacks.onMultipleStructuresUploaded.push(callback);
    }

    onMassSpecBtnClick(callback) {
        this.#callbacks.onMassSpecBtnClick.push(callback);
    }

    /**
     * Callback to be called when the user clicks on "save" button.
     * @param {function} callback
     */
    onSaveOrUpdate(callback) {
        this.#callbacks.onSaveOrUpdate.push(callback);
    }

    setVisible(visible) {
        this.#html.setVisible(visible);
    }

    /** @param {SubstanceExtractionParams} extractionParams */
    setExtractionParams(extractionParams){
        this.#extractionParams = extractionParams;
    }

    /**
     * Some components, like color palette or upload structure component can not be initialized before
     * the html component is shown.
     * Color palette is a custom web element, so there is nothing to do here, except just taking this element.
     * Upload structure component is a regular Component, so we need to initialize it here using its parent element.
     * @private
     */
    #initHtmlDependantComponents() {
        this.#colorPalette = this.#html.getColorPalette();
        this.#uploadStructureComponent.init(this.#html.getStructureComponentParentEl());
    }

    #setEventListeners() {
        // form inputs
        this.#html.onAliasInput(this.#onAliasChange.bind(this));
        this.#html.onMfOrEmwInput(this.#onMfOrEmwChange.bind(this));
        // buttons
        this.#html.onOkBtnClick(this.#onOkBtnClick.bind(this));
        this.#html.onCancel(this.#onCancel.bind(this));
        this.#html.onMassSpecBtnClick(this.#onMassSpecBtnClick.bind(this));
        this.#html.onDeleteBtnClick(this.#onDeleteBtnClick.bind(this));
        // structure upload component
        this.#uploadStructureComponent.onBeforeProcessingContentToUpload(this.#onBeforeProcessingContentToUpload.bind(this));
        this.#uploadStructureComponent.onStructureUploadStarted(this.#onStructureUploadStarted.bind(this));
        this.#uploadStructureComponent.onStructureUploaded(this.#onStructureUploaded.bind(this));
        this.#uploadStructureComponent.onStructureUploadError(this.#onStructureUploadError.bind(this));
        this.#uploadStructureComponent.onImportClicked(this.#onImportClicked.bind(this));
        // color picker
        this.#html.onColorPickerClick(this.#onColorPickerClick.bind(this));
        this.#colorPalette.addEventListener('colorChanged', this.#onChangeColor.bind(this));
        // color palette doesn't close automatically when user clicks out of its area (should we fix it?),
        // so we need to listen to "onClose" event and close it manually
        this.#colorPalette.addEventListener('onClose', () => this.#colorPalette.hide());
        // notification dialog (used for showing structure parsing errors)
        // every time we show notification, we also hide current dialog, and show it back when user clicks on "ok" button
        this.#notificationDialog.onOk(() => this.#html.setVisible(true));
        this.#notificationDialog.onCancel(() => this.#html.setVisible(true));
    }

    #onAliasChange(value) {
        // update form-data with alias "as-is", validation is not performed at this point
        this.#formData.alias = value;
        // update substance color if it was not changed manually
        this.#onDisplayNameChange();
    }

    #onMfOrEmwChange(value) {
        // Both, chemical formula and emw can't contain commas, but in case when emw is a decimal number,
        // it should have a dot as a separator.
        value = value.replace(",", ".");
        if (value.length === 0 || isNaN(+value))
            this.#formData.mfOrEmw = value; // value is MF (string)
        else
            this.#formData.mfOrEmw = +value; // value is EMW (number)
        // at this stage substance color can change only if it was not changed manually and alias is not specified
        if (!this.#formData.alias)
            this.#onDisplayNameChange();
    }

    /**
     * Every time the display name is changed we need to update the substance color.
     */
    #onDisplayNameChange() {
        if (this.#formData.color) // do not update color if it was changed manually
            return;
        const displayName = this.#formData.getSubstanceDisplayName();
        const color = this.#aliasColorScheme.getColor(displayName);
        this.#html.setSubstanceColor(color);
    }

    #getCurrentSubstanceColor() {
        return this.#formData.color || this.#aliasColorScheme.getColor(this.#formData.getSubstanceDisplayName());
    }

    async #onOkBtnClick() {
        if (await this.#validateSubstance()) {
            try {
                this.#setAsyncTaskInProgress(true);
                for (const cb of this.#callbacks.onSaveOrUpdate) {
                    await cb(this.#createExtractionParams());
                }
            } finally {
                this.#setAsyncTaskInProgress(false);
            }
        }
    }

    /**
     * This method is called when the user clicks on "cancel" button or pressing "ESC" key.
     */
    async #onCancel() {
        // if user presses "ESC" key, and color-palette is visible, then, probably, he wants to close color-palette
        // instead of dialog.
        if (!this.#colorPalette.hasAttribute("hidden"))
            this.#colorPalette.hide();
        else
            this.#html.remove();
    }

    async #onImportClicked() {
        this.#html.setVisible(false);
        const injectionSearchDialog = new InjectionSearchDialogCmp(new InjectionSearchRepository(
            this.#injectionRepository, this.#structureSvgLoader, this.#userSettings, this.#importTarget.orgId,
            this.#importTarget.injectionId));
        injectionSearchDialog.onCancel(() => this.#html.setVisible(true));
        injectionSearchDialog.onInjectionSelected(analytes => this.#onAnalytesImported(analytes));
        await injectionSearchDialog.show();
    }

    async #onAnalytesImported(analytes) {
        const structures = new StructureMetaList(analytes.map(StructureMeta.fromAnalyte));
        try {
            const results = await Promise.all(this.#callbacks.onMultipleStructuresUploaded.map(cb => cb(structures)));
            if (results.some(result => result === false)) {
                this.#html.setVisible(true);
                return false;
            }
            return true;
        } catch (e) {
            this.#html.setVisible(true);
            throw e;
        }
    }

    async #onMassSpecBtnClick() {
        let triggerCallback = true;
        if (!this.#formData.isReadOnly())
            triggerCallback = await this.#validateSubstance();
        if (triggerCallback)
            this.#callbacks.onMassSpecBtnClick.forEach(cb => cb(
                this.#createExtractionParams(), this.#getCurrentSubstanceColor()));
    }

    async #onDeleteBtnClick() {
        this.#callbacks.onDelete.forEach(cb => cb(this.#formData));
    }

    #createExtractionParams() {
        const substanceFromForm = this.#formData.getSubstanceJson();
        if(!substanceFromForm.hasSameComposition(this.#extractionParams.substance))
            return SubstanceExtractionParams.default(substanceFromForm);
        else
            return new SubstanceExtractionParams(substanceFromForm, this.#extractionParams.props);
    }

    /**
     * This method is called when the user clicks on small rectangle next to the ""
     * @param color
     */
    #onColorPickerClick(color) {
        this.#colorPalette.show({color});
    }

    #onBeforeProcessingContentToUpload() {
        if (this.#formData.isReadOnly()){
            this.#uploadStructureComponent.setDisabled(true);
            this.#notificationDialog.showText(Messages.COMMON.CANT_EDIT_IN_READ_ONLY_MODE);
        }
    }
    /**
     * Disables the dialog controls until the request is completed
     */
    #onStructureUploadStarted() {
        this.#setAsyncTaskInProgress(true);
    }

    /**
     * Toggles dialog controls "disabled" state
     * @param {boolean} inProgress
     */
    #setAsyncTaskInProgress(inProgress) {
        this.#uploadStructureComponent.setDisabled(inProgress);
        this.#html.setAsyncTaskInProgress(inProgress);
        if (!inProgress)
            this.#html.setMfOrEmwInputDisabled(this.#formData.struct != null);
    }

    /**
     * When the request is completed, we could have different results depending on how many structures were uploaded.
     * no structures parsed - shake the upload-structure component
     * one structure parsed - load svg and disable mfOrEmw input
     * multiple structures parsed - call onMultipleStructuresUploaded callbacks, passing the list of parsed structures
     *    it may lead to multiple chromatogram extraction, but this process is controlled by the "upper" level
     * @param {StructureMetaList} structures
     */
    async #onStructureUploaded(structures) {
        this.#setAsyncTaskInProgress(false);
        switch (structures.size()) {
            case 0: {
                this.#uploadStructureComponent.shake();
                break;
            }
            case 1: {
                const structure = structures.getStructures()[0];
                await this.#updateFormWithStructureMeta(structure);
                this.#onDisplayNameChange();
                break;
            }
            default: {
                this.#callbacks.onMultipleStructuresUploaded.forEach(cb => cb(structures));
            }
        }
    }

    /** @param {StructureMeta} structure */
    async #updateFormWithStructureMeta(structure) {
        await this.#uploadStructureComponent.loadStructure(structure.structureSvgPath);
        if (structure.alias)
            this.#formData.alias = structure.alias;
        this.#formData.mfOrEmw = structure.mf || structure.emw;
        this.#formData.struct = structure.id;
        this.#html.fillInputs(this.#formData);
    }

    #onStructureUploadError(err) {
        this.#setAsyncTaskInProgress(false);
        if (err instanceof ValidationError) {
            this.#html.setVisible(false);
            this.#notificationDialog.showText(err.message);
        } else
            throw err;
    }

    /**
     * User manually changes substance color. Setting color property to the formData makes color fixed, so
     * when display name changes we will not change color.
     */
    #onChangeColor() {
        const color = this.#colorPalette.getCurrentColor();
        this.#formData.color = color;
        this.#html.setSubstanceColor(color);
    }

    /**
     * Validates the substance. Either shows errors in the form or throws an exception if error is not a
     * validation error.
     */
    async #validateSubstance() {
        try {
            this.#setAsyncTaskInProgress(true);
            await this.#substanceValidator.validate(this.#formData);
            return true;
        } catch (err) {
            if (err instanceof SubstanceValidationError) {
                this.#showValidationErrors(err);
                return false;
            }
            throw err;
        }
        finally {
            this.#setAsyncTaskInProgress(false);
        }
    }

    /** @param {SubstanceValidationError} validationError */
    #showValidationErrors(validationError) {
        validationError.errors.forEach(/**@type {FormValidationError} */err => {
            if (err.field.includes("alias"))
                this.#html.showAliasFieldError(err.message);
            if (err.field.includes("mf"))
                this.#html.showMfOrEmwFieldError(err.message);
        });
    }
}